/**
 * Per-file upload progress, without threading a callback through every
 * module's upload thunk.
 *
 * FileGalleryControl registers a listener for each File it hands to a page's
 * onUpload/onBulkUpload; the S3 PUT helpers (api/*Api.ts uploadToS3) spread
 * `s3ProgressConfig(file)` into their axios config, which reports bytes sent
 * for that same File object. Keyed by the File itself (WeakMap), so nothing
 * leaks and two galleries never see each other's uploads.
 */
type Listener = (loaded: number, total: number) => void;

const listeners = new WeakMap<Blob, Listener>();

/** Starts listening to one file's upload; returns the unsubscribe. */
export function trackUploadProgress(file: Blob, listener: Listener): () => void {
  listeners.set(file, listener);
  return () => {
    if (listeners.get(file) === listener) listeners.delete(file);
  };
}

export function reportUploadProgress(file: Blob, loaded: number, total?: number) {
  listeners.get(file)?.(loaded, total && total > 0 ? total : file.size);
}

/** Spread into an axios config for a PUT of `file` (e.g. to a pre-signed S3 URL). */
export const s3ProgressConfig = (file: Blob) => ({
  onUploadProgress: (e: { loaded: number; total?: number }) =>
    reportUploadProgress(file, e.loaded, e.total),
});
